import { dateToHourAndMinutes } from '@helpers/date';
import { AxfoodTimeSlotViewModel } from '@occ/api-client';
import { useTranslations } from 'next-intl';
import Text from '@atoms/Text/Text';
import {
  StyledRadio,
  StyledSlotTextInformation,
  StyledSlotTextInformationInnerWrapper,
  StyledSlotTextWrapper,
  StyledSlotTimeText,
  StyledTimeSlotItem,
} from './TimeSlot.styles';
import { useLayoutEffect, useRef } from 'react';

interface Props {
  slot: AxfoodTimeSlotViewModel;
  onClick: (slot: AxfoodTimeSlotViewModel | null) => void;
  selected: boolean;
}

const TimeSlot = ({ slot, onClick, selected }: Props) => {
  const { expressSlot, available, closeTimeFormatted } = slot;
  const t = useTranslations('slotSelector');
  const ref = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!selected || !ref.current) return;

    // Wait for layout to settle before scrolling within the
    // ScrollShadow's scrollable container (nearest block)
    // 100ms is long enough to ensure the layout has settled
    // and short enough to not be noticeable to the user
    const id = setTimeout(() => {
      ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(id);
  }, [selected, ref]);

  const isDisabled = !available && !selected;

  return (
    <StyledTimeSlotItem
      type="button"
      disabled={isDisabled}
      ref={ref}
      data-selected={selected}
      data-testid="time-slot-item"
      onClick={(e) => {
        e.preventDefault();
        if (!available) return;
        if (!selected) {
          onClick(slot);
        } else {
          onClick(null);
        }
      }}
    >
      <StyledSlotTextWrapper>
        <StyledSlotTextInformation>
          <StyledRadio
            disabled={isDisabled}
            checked={selected}
            onChange={() => {}}
            id={slot.code}
            variant="checkmark"
            tabIndex={-1}
          />
          <StyledSlotTextInformationInnerWrapper>
            <StyledSlotTimeText
              $disabled={isDisabled}
              color={isDisabled ? 'darkGray' : 'black'}
              type="subtitle"
              size="medium"
            >
              {`${dateToHourAndMinutes(slot.startTime)} - ${dateToHourAndMinutes(slot.endTime)}`}
            </StyledSlotTimeText>
            {expressSlot && (
              <Text type="body" size="small" color={!available ? 'darkGray' : 'black'}>
                {t('express')}
              </Text>
            )}
          </StyledSlotTextInformationInnerWrapper>
        </StyledSlotTextInformation>
        {selected && (
          <Text type="body" size="small" align="left">
            {t('changeOrder', { closeTimeFormatted })}
          </Text>
        )}
      </StyledSlotTextWrapper>
    </StyledTimeSlotItem>
  );
};

export default TimeSlot;
